import React, { useRef, useState } from "react";
import { axios } from "../../lib/axios";
import { downloadFile, errorMessage, saveBlob } from "../../lib/download";
import { t } from "../../i18n";

const FIELDS = [
  { key: "name", label: "الاسم" },
  { key: "job", label: "الوظيفة" },
  { key: "salary", label: "الراتب (بالألف دينار)" },
  { key: "phone", label: "الهاتف" },
];

const REASONS = {
  formula: "الصيغ غير مسموحة، اكتب القيمة نصاً",
  name_required: "الاسم مطلوب",
  name_long: "الاسم أطول من 255 حرفاً",
  job_required: "الوظيفة مطلوبة",
  job_unknown: "الوظيفة ليست من أنواع العمل المسجلة",
  salary_required: "الراتب مطلوب",
  salary_not_number: "الراتب ليس رقماً",
  salary_range: "الراتب خارج النطاق المسموح",
  phone_required: "الهاتف مطلوب",
  phone_invalid: "صيغة الهاتف غير صحيحة",
  save_failed: "تعذر حفظ الصف",
};

const STATUS_STYLE = {
  valid: "bg-emerald-50 text-emerald-800",
  imported: "bg-emerald-50 text-emerald-800",
  duplicate: "bg-amber-50 text-amber-800",
  error: "bg-red-50 text-red-700",
};
const STATUS_LABEL = {
  valid: "جاهز",
  imported: "تم الاستيراد",
  duplicate: "مكرر (يُتخطى)",
  error: "خطأ",
};

function reasonsOf(row) {
  const list = (row.errors || []).map((code) => t(REASONS[code] || code));
  if (row.status === "duplicate" && row.duplicate_of) {
    list.push(
      row.duplicate_of.source === "file"
        ? t("الهاتف مكرر في الملف (السطر {line})", { line: row.duplicate_of.line })
        : t("الهاتف مسجل للموظف {name} (رقم {id})", {
            name: row.duplicate_of.name,
            id: row.duplicate_of.id,
          })
    );
  }
  return list.join(" · ");
}

// Upload -> (map columns if needed) -> preview with row errors -> explicit confirmation -> summary.
const ImportEmployeesModal = ({ onClose, onImported }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const upload = async (chosen, chosenMapping) => {
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", chosen);
      if (chosenMapping) form.append("mapping", JSON.stringify(chosenMapping));
      const { data } = await axios.post("/employees/import/preview", form);
      setPreview(data);
      setMapping(data.mapping || {});
    } catch (err) {
      setPreview(null);
      setError(await errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onFile = (event) => {
    const chosen = event.target.files?.[0];
    setResult(null);
    setPreview(null);
    setFile(chosen || null);
    if (chosen) upload(chosen);
  };

  const confirm = async () => {
    if (busy || !preview?.import_id) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`/employees/import/${preview.import_id}/confirm`, {
        confirmed: true,
      });
      setResult(data.summary);
      onImported?.();
    } catch (err) {
      setError(await errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const downloadErrors = async () => {
    try {
      const response = await axios.get(`/employees/import/${preview.import_id}/errors`, {
        responseType: "blob",
      });
      saveBlob(response.data, "employee-import-errors.xlsx");
    } catch (err) {
      setError(await errorMessage(err));
    }
  };

  const downloadTemplate = async () => {
    try {
      await downloadFile("/employees/import/template", {}, "employees-template.xlsx");
    } catch (err) {
      setError(err.message);
    }
  };

  const columns = preview?.columns || [];
  const canMap = preview?.needs_mapping;
  const allMapped = FIELDS.every((f) => mapping[f.key] !== undefined && mapping[f.key] !== "");
  const summary = preview?.summary;
  const skipped = (summary?.duplicates || 0) + (summary?.errors || 0);

  return (
    <div className="modal-overlay">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-title"
        className="modal-panel max-w-4xl p-6"
      >
        <h2 id="import-title" className="text-xl font-bold text-brand-600">
          {t("استيراد موظفين من Excel")}
        </h2>

        {!result && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={downloadTemplate}
              className="rounded-lg border border-slate-300 bg-card px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              {t("تنزيل القالب")}
            </button>
            <label className="text-sm font-medium text-slate-700" htmlFor="import-file">
              {t("ملف Excel (xlsx)")}
            </label>
            <input
              id="import-file"
              ref={fileRef}
              type="file"
              accept=".xlsx"
              onChange={onFile}
              disabled={busy}
              className="text-sm"
            />
          </div>
        )}

        {error && (
          <p role="alert" className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            {error}
          </p>
        )}
        {busy && !result && (
          <p role="status" className="mt-3 text-sm text-slate-600">
            {t("جاري المعالجة...")}
          </p>
        )}

        {canMap && !result && (
          <div className="mt-4">
            <p className="text-sm text-slate-700">
              {t("لم نتعرف على أسماء الأعمدة. اختر العمود المناسب لكل حقل:")}
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {FIELDS.map((f) => (
                <label key={f.key} className="text-sm font-medium text-slate-700">
                  {t(f.label)}
                  <select
                    className="field mt-1"
                    value={mapping[f.key] ?? ""}
                    onChange={(e) =>
                      setMapping({
                        ...mapping,
                        [f.key]: e.target.value === "" ? undefined : Number(e.target.value),
                      })
                    }
                  >
                    <option value="">{t("— اختر —")}</option>
                    {columns.map((c) => (
                      <option key={c.index} value={c.index}>
                        {c.header}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <button
              type="button"
              disabled={!allMapped || busy}
              onClick={() => upload(file, mapping)}
              className="mt-3 rounded-lg bg-brand-600 px-4 py-2 font-medium text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {t("معاينة")}
            </button>
          </div>
        )}

        {preview && !canMap && !result && (
          <div className="mt-4">
            {preview.already_imported_at && (
              <p className="mb-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                {t("تم استيراد هذا الملف نفسه من قبل. الموظفون المكررون سيُتخطّون.")}
              </p>
            )}
            <ul className="flex flex-wrap gap-2 text-sm" aria-label={t("ملخص المعاينة")}>
              <li className="rounded-full bg-slate-100 px-3 py-1">
                {t("إجمالي الصفوف")}: {summary.total}
              </li>
              <li className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-800">
                {t("جاهز للاستيراد")}: {summary.valid}
              </li>
              <li className="rounded-full bg-amber-50 px-3 py-1 text-amber-800">
                {t("مكرر")}: {summary.duplicates}
              </li>
              <li className="rounded-full bg-red-50 px-3 py-1 text-red-700">
                {t("أخطاء")}: {summary.errors}
              </li>
            </ul>
            <div className="mt-3 max-h-80 overflow-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-100 text-slate-700">
                  <tr>
                    <th className="p-2">{t("السطر")}</th>
                    <th className="p-2">{t("الاسم")}</th>
                    <th className="p-2">{t("الوظيفة")}</th>
                    <th className="p-2">{t("الراتب (بالألف دينار)")}</th>
                    <th className="p-2">{t("الهاتف")}</th>
                    <th className="p-2">{t("الحالة")}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => (
                    <tr key={row.line} className="border-t border-slate-200 align-top">
                      <td className="p-2 text-center tabular-nums">{row.line}</td>
                      <td className="p-2">{row.name}</td>
                      <td className="p-2">{row.job}</td>
                      <td className="p-2 text-center tabular-nums">{row.salary}</td>
                      <td className="p-2 text-center tabular-nums" dir="ltr">
                        {row.phone}
                      </td>
                      <td className="p-2">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[row.status]}`}
                        >
                          {t(STATUS_LABEL[row.status])}
                        </span>
                        {reasonsOf(row) && (
                          <div className="mt-1 text-xs text-slate-600">{reasonsOf(row)}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-4" role="status">
            <p className="text-lg font-semibold text-slate-800">{t("اكتمل الاستيراد")}</p>
            <ul className="mt-2 flex flex-wrap gap-2 text-sm">
              <li className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-800">
                {t("تم استيراده")}: {result.imported}
              </li>
              <li className="rounded-full bg-amber-50 px-3 py-1 text-amber-800">
                {t("تم تخطيه")}: {result.skipped}
              </li>
              <li className="rounded-full bg-red-50 px-3 py-1 text-red-700">
                {t("فشل")}: {result.failed}
              </li>
            </ul>
          </div>
        )}

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          {(result ? result.skipped + result.failed > 0 : skipped > 0) && preview?.import_id && (
            <button
              type="button"
              onClick={downloadErrors}
              className="rounded-lg border border-slate-300 bg-card px-4 py-2 font-medium text-slate-700 hover:bg-slate-100"
            >
              {t("تنزيل تقرير الأخطاء")}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-card px-4 py-2 font-medium text-slate-700 hover:bg-slate-100"
          >
            {result ? t("إغلاق") : t("إلغاء")}
          </button>
          {preview && !canMap && !result && (
            <button
              type="button"
              onClick={confirm}
              disabled={busy || summary.valid === 0}
              className="rounded-lg bg-brand-600 px-4 py-2 font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-60"
            >
              {t("تأكيد استيراد {count} موظف", { count: summary.valid })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportEmployeesModal;
